import { AdCampaign, AlmanacFileSummary, MetaAdCampaignBidStrategy, MetaAdCampaignSpecialAdCategory } from 'types'
import { createTLStore, Editor, type TLShapeUtilConstructor } from '@tldraw/tldraw'
import { DEFAULT_LANGUAGE } from '../Wizard/Form/LanguageInputField/LanguageInput'
import { MetaAdCampaignWizardValues } from './MetaAdCampaignWizard'
import { resize } from 'components/VisualStudioPlayground/VisualEditor/helpers'
import { shapeUtils } from 'components/VisualStudioPlayground/VisualEditor/VisualEditor'
import { META_ADS_DESIGN_HEIGHT, META_ADS_DESIGN_WIDTH } from './constants'

export const getResizedSnapshot = async (snapshot: AlmanacFileSummary['content'], width: number, height: number) => {
  const store = createTLStore({ shapeUtils })
  const editor = new Editor({
    store,
    shapeUtils: shapeUtils as TLShapeUtilConstructor<any, any>[],
    tools: [],
    getContainer: () => document.createElement('div'),
  })
  try {
    editor.store.loadSnapshot(snapshot)
    resize(editor, { width, height })
    return editor.store.getSnapshot()
  } finally {
    editor.dispose()
  }
}

export const isUsingSpecialAdCategories = (specialAdCategories?: MetaAdCampaignSpecialAdCategory[]) => {
  if (!specialAdCategories?.length) return false
  if (specialAdCategories.length === 1 && specialAdCategories[0] === MetaAdCampaignSpecialAdCategory.NONE) return false

  return true
}

export const mapAdCampaignValuesToWizardValues = (adCampaign?: AdCampaign): Partial<MetaAdCampaignWizardValues> => ({
  link_to_promote: adCampaign?.campaignable.link_to_promote,
  objective: adCampaign?.campaignable.objective,
  special_ad_categories: (adCampaign?.campaignable?.special_ad_categories as MetaAdCampaignSpecialAdCategory[]) ?? [
    MetaAdCampaignSpecialAdCategory.NONE,
  ],

  pixel_platform_id: adCampaign?.campaignable.pixel_platform_id,
  pixel_conversion_event_name: adCampaign?.campaignable.pixel_conversion_event_name,

  age_min: adCampaign?.campaignable.age_min ?? 18,
  age_max: adCampaign?.campaignable.age_max ?? 65,
  gender_name: adCampaign?.campaignable.gender_name ?? 'All Genders',
  locale_names: adCampaign?.campaignable.locale_names ?? [DEFAULT_LANGUAGE],
  geo_locations: adCampaign?.campaignable.geo_locations ?? { cities: [], countries: [], regions: [] },

  ad_call_to_action: adCampaign?.campaignable.ad_call_to_action ?? undefined,
  selected_ads_count: adCampaign?.ads?.reduce((acc, ad) => acc + (ad.is_selected ? 1 : 0), 0) ?? 0,
  auto_pause: adCampaign?.auto_pause ?? false,

  currency_code: adCampaign?.campaignable.currency_code,
  weekly_spend: Math.round((adCampaign?.campaignable.daily_budget_offsetted ?? 0) * 7),
  bid_strategy: adCampaign?.campaignable.bid_strategy ?? MetaAdCampaignBidStrategy.LOWEST_COST_WITHOUT_CAP,
  bid_amount_offsetted: adCampaign?.campaignable.bid_amount_offsetted ?? null,
  start_time: adCampaign?.campaignable.start_time,
  end_time: adCampaign?.campaignable.end_time,

  name: adCampaign?.name ?? '',

  topic: adCampaign?.campaignable.ad_topic ?? undefined,
  caption: adCampaign?.campaignable.ad_caption ?? undefined,
  text_overlay: adCampaign?.campaignable.ad_headline ?? undefined,

  width: META_ADS_DESIGN_WIDTH,
  height: META_ADS_DESIGN_HEIGHT,
})

export const getCurrencySymbol = (currencyCode?: string | null): string => {
  if (!currencyCode) return '$'

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
  const parts = formatter.formatToParts(0)
  return parts.find((p) => p.type === 'currency')?.value ?? currencyCode
}
